import React, { ChangeEvent, useRef, useState } from "react";
import "react-calendar/dist/Calendar.css";
import { dateToString, getCalendarDate } from "@src/utils/date";
import {
  DatepickerSize,
  DatepickerStyledRoot,
} from "@src/components/elements/date-pickers/DatepickerStyledRoot";
import { StyledCalendar } from "@src/components/elements/date-pickers/StyledCalendar";
import {
  ActionMenu,
  ActionMenuTrigger,
} from "@src/components/elements/ActionMenu";
import styled from "styled-components/macro";
import { CloseIcon } from "@src/components/elements/assets";

const StyleRoot = styled.div<{ floating: boolean }>`
  .datepicker-action-menu {
    width: 100%;
  }
`;

export interface DatepickerProps {
  label?: string;
  labelType?: "normal" | "floating";
  name: string;
  className?: string;
  error?: string;
  isRequired?: boolean;
  onChange?: (value: string, event?: ChangeEvent) => void;
  defaultValue?: string | null;
  minDate?: string;
  isDisabled?: boolean;
  size?: DatepickerSize;
  inline?: boolean;
  hideError?: boolean;
  fullWidth?: boolean;
  isClearable?: boolean;
}

const isValidDateFormat = (dateStr: string | null | undefined): boolean => {
  if (!dateStr) return false;
  const regex = /^\d{4}-\d{2}-\d{2}$/;
  if (!regex.test(dateStr)) return false;

  const [year, month, day] = dateStr.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
};

export const Datepicker: React.FC<DatepickerProps> = ({
  label,
  labelType,
  className,
  error,
  isRequired,
  onChange,
  defaultValue,
  minDate,
  isDisabled,
  size,
  inline,
  hideError = false,
  fullWidth = false,
  isClearable = false,
}) => {
  // selectedDate is in the format yyyy-mm-dd
  const actionMenuEl = useRef<ActionMenuTrigger>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [selectedDate, setSelectedDate] = useState(
    isValidDateFormat(defaultValue) ? defaultValue : "",
  );
  const [inputRect, setInputRect] = useState<DOMRect | null>(null);

  React.useEffect(() => {
    if (isValidDateFormat(defaultValue)) {
      setSelectedDate(defaultValue);
    }
  }, [defaultValue]);

  const updateInputRect = React.useCallback(() => {
    if (inputRef.current) {
      setInputRect(inputRef.current.getBoundingClientRect());
    }
  }, []);

  React.useEffect(() => {
    updateInputRect();
    window.addEventListener("resize", updateInputRect);
    window.addEventListener("scroll", updateInputRect);
    return () => {
      window.removeEventListener("resize", updateInputRect);
      window.removeEventListener("scroll", updateInputRect);
    };
  }, [updateInputRect]);

  const contents = (
    <>
      {!labelType || labelType === "normal" ? (
        <label htmlFor={label}>
          {label} {isRequired && <span className="error">*</span>}
        </label>
      ) : null}
      <input
        type="date"
        style={{ paddingTop: "0.5rem", height: "44px", position: "relative" }}
        key={selectedDate}
        required={isRequired}
        className={`${error ? "field-error" : ""}`}
        defaultValue={selectedDate || undefined}
        onBlur={(evt) => {
          // format here is yyyy-mm-dd
          const dateString = evt.target.value;
          setSelectedDate(dateString);
          onChange ? onChange(dateString, evt) : null;
        }}
        onFocus={() => {
          actionMenuEl.current?.open();
        }}
        disabled={isDisabled}
        ref={inputRef}
      />
      {isClearable && (
        <CloseIcon
          onClick={(event) => {
            event.stopPropagation();
            setSelectedDate("");
            onChange ? onChange("") : null;
            actionMenuEl.current?.close();
          }}
          style={{
            position: "absolute",
            top: "calc(50% - 11px)",
            left: `${inputRect ? inputRect.width - 30 : 0}px`,
          }}
        />
      )}
      {labelType === "floating" ? (
        <label
          htmlFor={label}
          style={{
            position: "absolute",
            top: "0px",
            transform: "translateY(-50%)",
            backgroundColor: "var(--page-background)",
            left: "0.25rem",
            padding: "0 0.25rem",
          }}
        >
          {label} {isRequired && <span className="error">*</span>}
        </label>
      ) : null}
      {!hideError && <span className="error">{error}&nbsp;</span>}
    </>
  );

  return (
    <StyleRoot floating={labelType === "floating"} className={className}>
      <ActionMenu
        toggleOnClick={false}
        autocloseOnSelect={false}
        ref={actionMenuEl}
        actionButton={
          <DatepickerStyledRoot size={size} fullWidth={fullWidth}>
            {inline ? <div className="row">{contents}</div> : contents}
          </DatepickerStyledRoot>
        }
        className="datepicker-action-menu"
      >
        <StyledCalendar
          onChange={(value: Date, event: ChangeEvent) => {
            const d = dateToString(value);
            setSelectedDate(d);
            onChange ? onChange(d, event) : null;
            actionMenuEl.current?.close();
          }}
          className={error ? "error" : ""}
          value={getCalendarDate(selectedDate || undefined)}
          minDate={minDate ? getCalendarDate(minDate) : undefined}
          navigationLabel={({ date, locale }) =>
            `${date.toLocaleDateString(locale, {
              month: "short",
              year: "numeric",
            })}`
          }
          tileDisabled={isDisabled ? () => true : undefined}
        />
      </ActionMenu>
    </StyleRoot>
  );
};

Datepicker.displayName = "Datepicker";
